import { appEnv } from "@/config/env";

const DEFAULT_API_URL = "http://localhost:8080/api";
const TOKEN_STORAGE_KEY = "hc_token";
let authRedirectInProgress = false;

export type ApiError = {
  message: string;
  code?: string;
  status?: number;
  errors?: Record<string, string>;
};

export const getApiBaseUrl = () => {
  const baseUrl = appEnv.VITE_API_URL ?? DEFAULT_API_URL;
  return baseUrl.endsWith("/api") ? baseUrl : `${baseUrl}/api`;
};

export const getAuthToken = () => {
  if (typeof localStorage === "undefined") return null;
  return localStorage.getItem(TOKEN_STORAGE_KEY);
};

export const setAuthToken = (token: string) => {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
};

export const clearAuthToken = () => {
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem(TOKEN_STORAGE_KEY);
};

export const apiFetch = async <T>(
  path: string,
  options: RequestInit = {},
): Promise<T> => {
  const baseUrl = getApiBaseUrl();
  const url = path.startsWith("http")
    ? path
    : `${baseUrl}${path.startsWith("/") ? "" : "/"}${path}`;

  const headers = new Headers(options.headers);
  const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
  if (!isFormData && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const token = getAuthToken();
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const response = await fetch(url, { ...options, headers });
  const contentType = response.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const payload = isJson ? await response.json().catch(() => null) : await response.text().catch(() => null);

  if (!response.ok) {
    if (response.status === 401 && token) {
      clearAuthToken();
      if (typeof window !== "undefined" && !authRedirectInProgress) {
        authRedirectInProgress = true;
        if (window.location.pathname !== "/") {
          window.location.assign("/");
        } else {
          authRedirectInProgress = false;
        }
      }
    }

    const apiError: ApiError = {
      message: payload?.message ?? payload?.error ?? response.statusText ?? "Request failed",
      code: payload?.code,
      status: response.status,
      errors: payload?.errors,
    };
    throw apiError;
  }

  return payload as T;
};
